/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  CircularProgress,
  Tooltip,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  InfoOutlined,
  AccountBalance,
  AttachMoney,
} from '@mui/icons-material';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import useIsMobile from '../../hooks/useIsMobile';
import buyerClosingCostsService from '../../api/services/buyerClosingCosts';
import {
  ClosingCostPaymentMethod,
  DEFAULT_DOWN_PAYMENT_PERCENT,
  buyerPaidClosingCosts,
  cashToCloseFor,
  closingCostQuery,
  initialPaymentMethod,
} from './closingCostQuery';

interface ClosingCostEstimatorProps {
  propertyPrice: number;
  propertyTax?: number;
  address?: string;
  isAssumable?: boolean;
  openMortgageBalance?: number;
}

interface ClosingCostBreakdown {
  prepaidCosts: {
    insurance: number;
    taxes: number;
    interest: number;
    total: number;
  };
  titleFees: {
    homeownersPolicy: number;
    lendersPolicy: number;
    closingFee: number;
    titleBinder: number;
    titleSearch: number;
    titleExam: number;
    lenderCPL: number;
    total: number;
  };
  lenderFees: {
    appraisal: number;
    underwriting: number;
    processing: number;
    creditReport: number;
    survey: number;
    origination: number;
    pmi: number;
    total: number;
  };
  otherFees: {
    attorneyFee: number;
    brokerFee: number;
    deedTransferTax: number;
    recordingMortgage: number;
    recordingDeed: number;
    eRecordingFee: number;
    total: number;
  };
  total: number;
}

const ClosingCostEstimator: React.FC<ClosingCostEstimatorProps> = ({
  propertyPrice,
  propertyTax = 0,
  address,
  isAssumable = false,
  openMortgageBalance = 0,
}) => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<ClosingCostPaymentMethod>(
    initialPaymentMethod(isAssumable)
  );
  const [downPaymentPercent, setDownPaymentPercent] = useState(
    DEFAULT_DOWN_PAYMENT_PERCENT
  );
  const [costs, setCosts] = useState<ClosingCostBreakdown | null>(null);

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Fetch closing costs from backend
  useEffect(() => {
    const fetchCosts = async () => {
      if (!propertyPrice) return;

      setLoading(true);
      try {
        const data = await buyerClosingCostsService.find(
          closingCostQuery({
            propertyPrice,
            propertyTax,
            paymentMethod,
            downPaymentPercent,
            isAssumable,
          })
        );

        setCosts({
          prepaidCosts: data.prepaidCosts,
          titleFees: data.titleFees,
          lenderFees: data.lenderFees,
          otherFees: data.otherFees,
          total: data.total,
        });
      } catch (error) {
        console.error('Error fetching closing costs:', error);
        // Fall back to local calculation if service fails
        setCosts(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCosts();
  }, [
    propertyPrice,
    propertyTax,
    paymentMethod,
    downPaymentPercent,
    isAssumable,
  ]);

  // Down payment: the seller's equity for an assumable, the whole price for
  // cash, otherwise the chosen percentage.
  const cashToCloseInput = {
    propertyPrice,
    paymentMethod,
    downPaymentPercent,
    isAssumable,
    openMortgageBalance,
  };
  const downPayment = cashToCloseFor(cashToCloseInput);
  const buyerClosingCosts = costs ? buyerPaidClosingCosts(costs) : 0;
  const homepointFee = costs?.otherFees.brokerFee ?? 0;
  const totalCashToClose = cashToCloseFor({
    ...cashToCloseInput,
    closingCostsTotal: buyerClosingCosts,
  });

  if (!costs && !loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Unable to calculate closing costs at this time
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#f8f9fa',
        p: isMobile ? 2 : 2.25,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header */}
      {/* Payment Method and Down Payment Row */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* Payment Method Toggle - Hidden for assumable properties */}
        {!isAssumable && (
          <ToggleButtonGroup
            value={paymentMethod}
            exclusive
            onChange={(e, newValue) =>
              newValue && setPaymentMethod(newValue as 'Finance' | 'Cash')
            }
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: '#374151',
                borderColor: '#e5e7eb',
                py: 0.5,
                px: 1.5,
                '&.Mui-selected': {
                  backgroundColor: '#279AF1',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1976d2',
                  },
                },
              },
            }}
          >
            <ToggleButton value="Finance">
              <AccountBalance sx={{ mr: 0.5, fontSize: 18 }} />
              Finance
            </ToggleButton>
            <ToggleButton value="Cash">
              <AttachMoney sx={{ mr: 0.5, fontSize: 18 }} />
              Cash
            </ToggleButton>
          </ToggleButtonGroup>
        )}

        {/* Down Payment Selector (only for financing) */}
        {paymentMethod === 'Finance' && (
          <>
            <Typography
              variant="body2"
              sx={{ ml: 1, mr: 0.5, color: '#6b7280', fontSize: 13 }}
            >
              Down Payment:
            </Typography>
            <ToggleButtonGroup
              value={downPaymentPercent}
              exclusive
              onChange={(e, newValue) =>
                newValue && setDownPaymentPercent(newValue)
              }
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  color: '#374151',
                  borderColor: '#e5e7eb',
                  py: 0.5,
                  px: 1,
                  minWidth: 40,
                  '&.Mui-selected': {
                    backgroundColor: '#279AF1',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1976d2',
                    },
                  },
                },
              }}
            >
              <ToggleButton value={5}>5%</ToggleButton>
              <ToggleButton value={10}>10%</ToggleButton>
              <ToggleButton value={20}>20%</ToggleButton>
              <ToggleButton value={25}>25%</ToggleButton>
            </ToggleButtonGroup>
          </>
        )}
      </Box>

      {/* Summary Box */}
      <Box
        sx={{
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
          mb: 2,
          border: '1px solid #e5e7eb',
          backgroundColor: 'white',
        }}
      >
        {loading ? (
          <CircularProgress size={40} sx={{ color: '#279AF1', mb: 1 }} />
        ) : (
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#279AF1' }}>
            {formatCurrency(totalCashToClose)}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: '#6b7280' }}>
          Total Cash to Close
        </Typography>
        <Divider sx={{ my: 1.5, borderColor: '#e5e7eb' }} />
        <Stack spacing={1}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {isAssumable
                  ? 'Cash to Seller'
                  : paymentMethod === 'Cash'
                  ? 'Purchase Price'
                  : `Down Payment (${downPaymentPercent}%)`}
              </Typography>
              {isAssumable && (
                <CustomTooltip
                  title={`The difference between the sale price (${formatCurrency(
                    propertyPrice
                  )}) and the remaining loan balance (${formatCurrency(
                    openMortgageBalance
                  )}). This is the seller's equity that you pay directly to them.`}
                  placement="top"
                  width="280px"
                >
                  <InfoOutlined sx={{ fontSize: 14, color: '#9ca3af' }} />
                </CustomTooltip>
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
              {formatCurrency(downPayment)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>
              Closing Costs
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
              {formatCurrency(buyerClosingCosts)}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Homepoint's fee: the contract has the seller pay it, so it stays out of the buyer's totals */}
      {costs && !loading && homepointFee > 0 && (
        <Box
          data-testid="seller-paid-fee"
          sx={{
            borderRadius: 2,
            p: 1.5,
            bgcolor: '#eef6fe',
            border: '1px solid #d6e9fb',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: '#111827' }}
              >
                Homepoint fee
              </Typography>
              <CustomTooltip
                title="Homepoint's buyer fee for the payment method chosen above (1.25% base, +0.25% with an inspection, cash 0.25% less, VA 0.25% more, $5,000 minimum). A traditional buyer's agent charges 3%."
                placement="top"
                width="280px"
              >
                <InfoOutlined sx={{ fontSize: 14, color: '#9ca3af' }} />
              </CustomTooltip>
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: '#111827' }}
            >
              {formatCurrency(homepointFee)}
            </Typography>
          </Box>
          <Typography
            variant="caption"
            component="p"
            sx={{ color: '#374151', mt: 0.5, lineHeight: 1.45 }}
          >
            Paid by the seller. We write it into your purchase contract, so it
            isn&apos;t in your closing costs or cash to close above.
          </Typography>
        </Box>
      )}

      {/* Detailed Breakdown */}
      {costs && !loading && (
        <Box
          sx={{
            mt: 2,
            pb: 2,
            bgcolor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            p: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#111827' }}>
            Cost Breakdown
          </Typography>

          {/* Prepaid Costs */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#374151' }}>
              Prepaid Items
            </Typography>
            <Stack spacing={0.5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Property Insurance (12 mo)
                  </Typography>
                  <CustomTooltip
                    title="Homeowners insurance protects your home from damage. Lenders require you to prepay a full year upfront to ensure continuous coverage."
                    placement="top"
                    width="280px"
                  >
                    <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                  </CustomTooltip>
                </Box>
                <Typography variant="caption" sx={{ color: '#111827' }}>
                  {formatCurrency(costs?.prepaidCosts.insurance || 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280' }}>
                    Property Taxes (6 mo)
                  </Typography>
                  <CustomTooltip
                    title="Property taxes fund local services like schools and roads. You'll prepay 6 months to ensure taxes are covered when due."
                    placement="top"
                    width="280px"
                  >
                    <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                  </CustomTooltip>
                </Box>
                <Typography variant="caption" sx={{ color: '#111827' }}>
                  {formatCurrency(costs?.prepaidCosts.taxes || 0)}
                </Typography>
              </Box>
              {paymentMethod === 'Finance' && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Prepaid Interest</Typography>
                    <CustomTooltip
                      title="Interest charged from your closing date until your first mortgage payment. For example, if you close on the 15th, you pay interest for the remaining 15 days of that month."
                      placement="top"
                      width="280px"
                    >
                      <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                    </CustomTooltip>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#111827' }}>
                    {formatCurrency(costs?.prepaidCosts.interest || 0)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Lender Fees or Assumption Fee */}
          {(paymentMethod === 'Finance' || isAssumable) && costs && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#374151' }}>
                {isAssumable ? 'Assumption Fees' : 'Lender Fees'}
              </Typography>
              <Stack spacing={0.5}>
                {isAssumable ? (
                  // Assumption Fee for assumable loans
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>
                        Loan Assumption Fee
                      </Typography>
                      <CustomTooltip
                        title="Fee charged by the existing lender to transfer the mortgage to you. This replaces all the typical lender fees since you're taking over an existing loan."
                        placement="top"
                        width="280px"
                      >
                        <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                      </CustomTooltip>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#111827' }}>
                      {formatCurrency(costs?.lenderFees.processing || 0)}
                    </Typography>
                  </Box>
                ) : (
                  // Regular lender fees for new loans
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          Loan Origination (1%)
                        </Typography>
                        <CustomTooltip
                          title="This is the lender's fee for processing your loan application and creating your mortgage. Typically 1% of your loan amount."
                          placement="top"
                          width="280px"
                        >
                          <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                        </CustomTooltip>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#111827' }}>
                        {formatCurrency(costs?.lenderFees.origination || 0)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>Appraisal</Typography>
                        <CustomTooltip
                          title="A professional assessment of the home's value. The lender requires this to ensure the property is worth what you're paying."
                          placement="top"
                          width="280px"
                        >
                          <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                        </CustomTooltip>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#111827' }}>
                        {formatCurrency(costs?.lenderFees.appraisal || 0)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>Underwriting</Typography>
                        <CustomTooltip
                          title="The lender's fee for reviewing your financial information and approving your loan. This covers the work of verifying your income, assets, and creditworthiness."
                          placement="top"
                          width="280px"
                        >
                          <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                        </CustomTooltip>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#111827' }}>
                        {formatCurrency(costs?.lenderFees.underwriting || 0)}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>
          )}

          {/* Other Fees */}
          {costs && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#374151' }}>
                Other Fees
              </Typography>
              <Stack spacing={0.5}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>Survey</Typography>
                    <CustomTooltip
                      title="A professional survey maps your property boundaries. This confirms exactly what land you're buying and can reveal any encroachments or boundary issues."
                      placement="top"
                      width="280px"
                    >
                      <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                    </CustomTooltip>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#111827' }}>
                    {formatCurrency(costs?.lenderFees.survey || 0)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                      Recording & Transfer
                    </Typography>
                    <CustomTooltip
                      title="Government fees to officially record your deed and mortgage with the county. This creates a public record that you own the property."
                      placement="top"
                      width="280px"
                    >
                      <InfoOutlined sx={{ fontSize: 12, color: '#9ca3af' }} />
                    </CustomTooltip>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#111827' }}>
                    {formatCurrency(
                      (costs?.otherFees.deedTransferTax || 0) +
                        (costs?.otherFees.recordingDeed || 0) +
                        (costs?.otherFees.eRecordingFee || 0)
                    )}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ClosingCostEstimator;
